// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API module for locking an accounting close run.
 * Provides type-safe close run locking with Zod validation.
 */
import * as z from 'zod';
import { closeRunResponseSchema } from '@/accounting/api/types';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';

/**
 * Schema for validating the response from the lock close run API endpoint.
 * Includes both the close run data and metadata about the request.
 */
const lockCloseRunResponseSchema = z
  .object({
    data: closeRunResponseSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, closeRun: data }));

/**
 * Type definition for the lock close run API response.
 * Contains the locked close run data and request metadata.
 */
type LockCloseRunResponse = z.infer<typeof lockCloseRunResponseSchema>;

/**
 * Request body for locking a close run.
 */
interface LockCloseRunRequest {
  signature: string;
  notes?: string;
}

/**
 * Locks an accounting close run, preventing further modifications.
 *
 * @param id - The unique identifier of the close run to lock
 * @param signature - The user's signature confirming the lock
 * @param notes - Optional notes about the lock
 * @returns Promise resolving to the locked close run data and request metadata
 * @throws Will throw an error if the close run is not found, already locked,
 *         API request fails, or response validation fails
 */
export async function lockCloseRun(
  id: string,
  signature: string,
  notes?: string
): Promise<LockCloseRunResponse> {
  const url = `/v1/accounting/close-runs/${id}/lock`;
  const requestBody: LockCloseRunRequest = { signature };
  if (notes) {
    requestBody.notes = notes;
  }
  const response = await api.post(url, requestBody);
  return parseResponse(lockCloseRunResponseSchema, response.data, {
    schemaName: 'LockCloseRun',
    endpoint: url,
  });
}
